'use client';
import React, { useState, useRef, useCallback } from 'react';

type OutputFormat = 'image/jpeg' | 'image/png' | 'image/webp';

interface FormatOption {
  value: OutputFormat;
  label: string;
  ext: string;
  supportsTransparency: boolean;
}

const FORMAT_OPTIONS: FormatOption[] = [
  { value: 'image/jpeg', label: 'JPG', ext: 'jpg', supportsTransparency: false },
  { value: 'image/png', label: 'PNG', ext: 'png', supportsTransparency: true },
  { value: 'image/webp', label: 'WebP', ext: 'webp', supportsTransparency: true },
];

interface SourceImage {
  file: File;
  url: string;
  size: number;
  width: number;
  height: number;
  hasTransparency: boolean;
  format: string;
}

interface ConvertedResult {
  url: string;
  size: number;
  blob: Blob;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function getFormatLabel(mimeType: string): string {
  const map: Record<string, string> = {
    'image/jpeg': 'JPG',
    'image/png': 'PNG',
    'image/webp': 'WebP',
    'image/gif': 'GIF',
    'image/bmp': 'BMP',
    'image/tiff': 'TIFF',
  };
  return map[mimeType] ?? mimeType.split('/')[1]?.toUpperCase() ?? 'Unknown';
}

function detectTransparency(img: HTMLImageElement, canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;
  canvas.width = Math.min(img.naturalWidth, 64);
  canvas.height = Math.min(img.naturalHeight, 64);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  try {
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) return true;
    }
  } catch {
    return false;
  }
  return false;
}

export default function ImageFormatConverterTool() {
  const [source, setSource] = useState<SourceImage | null>(null);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('image/jpeg');
  const [quality, setQuality] = useState(92);
  const [result, setResult] = useState<ConvertedResult | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewTab, setPreviewTab] = useState<'original' | 'converted'>('original');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const loadImage = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please upload a valid image file (JPG, PNG, WebP, GIF, BMP, etc.).');
      return;
    }
    setError(null);
    setResult(null);
    setPreviewTab('original');

    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      const canvas = canvasRef.current ?? document.createElement('canvas');
      const hasTransparency = detectTransparency(img, canvas);
      setSource({
        file,
        url,
        size: file.size,
        width: img.naturalWidth,
        height: img.naturalHeight,
        hasTransparency,
        format: file.type,
      });
    };
    img.onerror = () => setError('Failed to load image. Please try a different file.');
    img.src = url;
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) loadImage(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) loadImage(file);
  };

  const handleConvert = useCallback(async () => {
    if (!source) return;
    setProcessing(true);
    setError(null);
    setResult(null);

    try {
      await new Promise<void>((resolve, reject) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          if (!ctx) { reject(new Error('Canvas context unavailable')); return; }

          // Fill white background for JPG (no transparency support)
          if (outputFormat === 'image/jpeg') {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }

          ctx.drawImage(img, 0, 0);

          const qualityValue = outputFormat === 'image/png' ? undefined : quality / 100;

          canvas.toBlob(
            (blob) => {
              if (!blob) { reject(new Error('Conversion failed — canvas produced no output.')); return; }
              const url = URL.createObjectURL(blob);
              setResult({ url, size: blob.size, blob });
              setPreviewTab('converted');
              resolve();
            },
            outputFormat,
            qualityValue,
          );
        };
        img.onerror = () => reject(new Error('Failed to reload image for conversion.'));
        img.src = source.url;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Conversion failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  }, [source, outputFormat, quality]);

  const handleDownload = () => {
    if (!result || !source) return;
    const fmt = FORMAT_OPTIONS.find((f) => f.value === outputFormat);
    const baseName = source.file.name.replace(/\.[^.]+$/, '');
    const a = document.createElement('a');
    a.href = result.url;
    a.download = `${baseName}_converted.${fmt?.ext ?? 'jpg'}`;
    a.click();
  };

  const handleReset = () => {
    setSource(null);
    setResult(null);
    setError(null);
    setPreviewTab('original');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const selectedFmt = FORMAT_OPTIONS.find((f) => f.value === outputFormat)!;
  const isSameFormat = source?.format === outputFormat;
  const transparencyWarning =
    source?.hasTransparency && outputFormat === 'image/jpeg';
  const sizeDiff = result && source ? result.size - source.size : 0;

  return (
    <div className="space-y-6">
      {/* Hidden canvas for transparency detection */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Error banner */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Upload zone */}
      {!source ? (
        <div
          className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${
            isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/50'
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
          </div>
          <p className="font-semibold text-gray-800 mb-1">Upload Image</p>
          <p className="text-gray-500 text-sm mb-3">Drag & drop or click to browse</p>
          <p className="text-gray-400 text-xs">Supports JPG, PNG, WebP, GIF, BMP and more</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      ) : (
        <div className="space-y-5">
          {/* Before / After Preview */}
          <div className="rounded-2xl border border-gray-100 overflow-hidden bg-gray-50">
            <div className="flex border-b border-gray-100">
              <button
                onClick={() => setPreviewTab('original')}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                  previewTab === 'original' ?'bg-white text-blue-600 border-b-2 border-blue-600' :'text-gray-500 hover:text-gray-700'
                }`}
              >
                Original ({getFormatLabel(source.format)})
              </button>
              <button
                onClick={() => setPreviewTab('converted')}
                disabled={!result}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  previewTab === 'converted' ?'bg-white text-blue-600 border-b-2 border-blue-600' :'text-gray-500 hover:text-gray-700'
                }`}
              >
                Converted ({selectedFmt.label})
              </button>
            </div>

            <div className="h-56 sm:h-72 flex items-center justify-center p-4 bg-[repeating-conic-gradient(#f0f0f0_0%_25%,transparent_0%_50%)_0_0/16px_16px]">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={previewTab === 'converted' && result ? result.url : source.url}
                alt={previewTab === 'converted' ? 'Converted image preview' : 'Original image preview'}
                className="max-h-full max-w-full object-contain rounded-lg shadow-sm"
              />
            </div>

            <div className="flex items-center justify-between px-4 py-2.5 bg-white border-t border-gray-100">
              <span className="text-xs text-gray-500 truncate max-w-[200px]">{source.file.name}</span>
              <span className="text-xs font-semibold text-gray-700">
                {previewTab === 'converted' && result
                  ? formatBytes(result.size)
                  : formatBytes(source.size)}
              </span>
            </div>
          </div>

          {/* Transparency warning */}
          {transparencyWarning && (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl p-3.5">
              <svg className="w-4.5 h-4.5 text-amber-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <p className="text-amber-700 text-xs leading-relaxed">
                Your image has transparent areas. JPG doesn&apos;t support transparency — transparent pixels will be replaced with a white background. Choose PNG or WebP to preserve transparency.
              </p>
            </div>
          )}

          {/* File info row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-gray-500 text-xs mb-0.5">Original</p>
              <p className="font-bold text-gray-800 text-sm">{formatBytes(source.size)}</p>
              <p className="text-gray-400 text-xs mt-0.5">{getFormatLabel(source.format)}</p>
            </div>
            <div className={`rounded-xl p-3 text-center ${result ? 'bg-blue-50' : 'bg-gray-50'}`}>
              <p className="text-gray-500 text-xs mb-0.5">Converted</p>
              <p className={`font-bold text-sm ${result ? 'text-blue-600' : 'text-gray-400'}`}>
                {result ? formatBytes(result.size) : '—'}
              </p>
              <p className="text-gray-400 text-xs mt-0.5">{selectedFmt.label}</p>
            </div>
            <div className={`rounded-xl p-3 text-center ${result ? (sizeDiff < 0 ? 'bg-green-50' : 'bg-orange-50') : 'bg-gray-50'}`}>
              <p className="text-gray-500 text-xs mb-0.5">Size change</p>
              <p className={`font-bold text-sm ${result ? (sizeDiff < 0 ? 'text-green-600' : 'text-orange-500') : 'text-gray-400'}`}>
                {result
                  ? sizeDiff < 0
                    ? `−${formatBytes(Math.abs(sizeDiff))}`
                    : `+${formatBytes(sizeDiff)}`
                  : '—'}
              </p>
              <p className="text-gray-400 text-xs mt-0.5">
                {result && source
                  ? `${Math.abs(Math.round(((source.size - result.size) / source.size) * 100))}%`
                  : ''}
              </p>
            </div>
          </div>

          {/* Image dimensions */}
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
            </svg>
            <span>{source.width} × {source.height} px</span>
            {source.hasTransparency && (
              <>
                <span className="text-gray-300">·</span>
                <span className="text-purple-500 font-medium">Has transparency</span>
              </>
            )}
          </div>

          {/* Output format selector */}
          <div>
            <p className="text-sm font-semibold text-gray-800 mb-3">Output Format</p>
            <div className="grid grid-cols-3 gap-3">
              {FORMAT_OPTIONS.map((fmt) => (
                <button
                  key={fmt.value}
                  onClick={() => { setOutputFormat(fmt.value); setResult(null); setPreviewTab('original'); }}
                  className={`relative rounded-xl border-2 p-3 text-center transition-all ${
                    outputFormat === fmt.value
                      ? 'border-blue-500 bg-blue-50' :'border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/30'
                  }`}
                >
                  <p className={`font-bold text-base ${outputFormat === fmt.value ? 'text-blue-600' : 'text-gray-700'}`}>
                    {fmt.label}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {fmt.supportsTransparency ? 'Transparency ✓' : 'No transparency'}
                  </p>
                  {outputFormat === fmt.value && (
                    <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Quality slider (not for PNG) */}
          {outputFormat !== 'image/png' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="font-semibold text-gray-800 text-sm">Quality</label>
                <span className="text-blue-600 font-bold text-sm bg-blue-50 px-2.5 py-0.5 rounded-lg">{quality}%</span>
              </div>
              <input
                type="range"
                min={10}
                max={100}
                step={1}
                value={quality}
                onChange={(e) => { setQuality(Number(e.target.value)); setResult(null); setPreviewTab('original'); }}
                className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gray-200 accent-blue-600"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>Smaller file</span>
                <span>Higher quality</span>
              </div>
            </div>
          )}

          {/* Same format notice */}
          {isSameFormat && (
            <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
              The output format matches the input format. You can still convert to re-encode the file.
            </p>
          )}

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleConvert}
              disabled={processing}
              className="flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-semibold text-white text-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #2563EB 0%, #1d4ed8 60%, #1e40af 100%)' }}
            >
              {processing ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Converting…
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                  </svg>
                  Convert to {selectedFmt.label}
                </>
              )}
            </button>

            {result && (
              <button
                onClick={handleDownload}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-semibold text-white text-sm bg-green-600 hover:bg-green-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Download {selectedFmt.label}
              </button>
            )}

            <button
              onClick={handleReset}
              className="sm:w-auto px-5 py-3 rounded-xl font-semibold text-gray-600 text-sm bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
